// Answer controls for "tick all that apply" and "put in order" questions
// (2026-09-28). Both are controlled by the practice screen's own answer
// text, which holds the chosen ids one per line -- so the screen's existing
// reset-per-question and retry-with-the-same-key behaviour applies to them
// unchanged. Both send {responseKind: 'ordered_set', value: {order: ids}}.

import { MathText } from '../../components/MathText';
import { splitAnswer, type ListOption } from './listAnswer';

export function TickAllAnswer({
  options,
  value,
  disabled,
  onChange,
}: {
  options: ListOption[];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const ticked = new Set(splitAnswer(value));
  return (
    <fieldset className="grid gap-2.5">
      <legend className="mb-2.5 font-semibold">Tick every right answer</legend>
      {options.map((option) => {
        const isTicked = ticked.has(option.id);
        return (
          <label
            key={option.id}
            className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border-2 px-4 text-xl font-semibold transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-forest-600 ${
              isTicked
                ? 'border-forest-600 bg-sage text-forest-800'
                : 'border-line bg-white text-ink hover:border-line-strong'
            }`}
          >
            <input
              type="checkbox"
              checked={isTicked}
              disabled={disabled}
              className="size-5 accent-forest-600"
              onChange={() => {
                const next = options
                  .map((o) => o.id)
                  .filter((id) =>
                    id === option.id ? !isTicked : ticked.has(id),
                  );
                onChange(next.join('\n'));
              }}
            />
            <MathText text={option.label} />
          </label>
        );
      })}
    </fieldset>
  );
}

export function OrderAnswer({
  items,
  value,
  disabled,
  onChange,
}: {
  items: ListOption[];
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const chosen = splitAnswer(value);
  const labelFor = (id: string) =>
    items.find((item) => item.id === id)?.label ?? id;
  const remaining = items.filter((item) => !chosen.includes(item.id));
  return (
    <div className="grid gap-3">
      <p className="font-semibold">
        Tap them in order, starting with the first.
      </p>
      <ol aria-label="Your order" className="grid gap-2">
        {chosen.map((id, index) => (
          <li
            key={id}
            className="flex min-h-12 items-center gap-3 rounded-xl border-2 border-forest-600 bg-sage px-4 text-xl font-semibold text-forest-800"
          >
            <span aria-hidden="true" className="text-base text-forest-600">
              {index + 1}.
            </span>
            <MathText text={labelFor(id)} />
          </li>
        ))}
      </ol>
      {remaining.length > 0 && (
        <div role="group" aria-label="Still to place" className="grid gap-2">
          {remaining.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={disabled}
              className="flex min-h-12 items-center rounded-xl border-2 border-line bg-white px-4 text-left text-xl font-semibold text-ink hover:border-line-strong"
              onClick={() => {
                onChange([...chosen, item.id].join('\n'));
              }}
            >
              <MathText text={item.label} />
            </button>
          ))}
        </div>
      )}
      {chosen.length > 0 && (
        <button
          type="button"
          disabled={disabled}
          className="dm-link-quiet w-fit"
          onClick={() => {
            onChange('');
          }}
        >
          Start again
        </button>
      )}
    </div>
  );
}
